import type { AppLocation, NavigationTarget } from './types';
import { isEqual } from 'es-toolkit';
import { createPath, parsePath } from 'history';

const trimTrailingSlash = (pathname: string) => (pathname === '/' ? pathname : pathname.replace(/\/+$/, ''));

export const createNavigationPath = (to: NavigationTarget): string => (typeof to === 'string' ? to : createPath(to));

export const hasNavigationPathname = (to: NavigationTarget): boolean =>
    typeof to === 'string' ? parsePath(to).pathname !== undefined : to.pathname !== undefined;

export const getNavigationPathname = (to: NavigationTarget): string =>
    parsePath(createNavigationPath(to)).pathname ?? '/';

export const isRoutePath = (pathname: string, routePath: string): boolean => {
    const normalizedPathname = trimTrailingSlash(pathname) || '/';
    const normalizedRoutePath = trimTrailingSlash(routePath) || '/';
    if (normalizedRoutePath === '/') return normalizedPathname.startsWith('/');
    return normalizedPathname === normalizedRoutePath || normalizedPathname.startsWith(`${normalizedRoutePath}/`);
};

export function resolvePath(to: NavigationTarget, fromPathname: string): NavigationTarget {
    if (typeof to !== 'string') {
        if (!to.pathname || to.pathname.startsWith('/')) return to;
        return { ...to, pathname: resolvePathname(to.pathname, fromPathname) };
    }

    const parsed = parsePath(to);
    if (!parsed.pathname || parsed.pathname.startsWith('/')) return to;
    return createPath({ ...parsed, pathname: resolvePathname(parsed.pathname, fromPathname) });
}

export function resolvePathname(toPathname: string, fromPathname: string): string {
    const normalizedFrom = trimTrailingSlash(fromPathname) || '/';
    const fromSegments = normalizedFrom === '/' ? [''] : normalizedFrom.split('/');
    const toSegments = toPathname.split('/');

    for (const segment of toSegments) {
        if (segment === '..') {
            if (fromSegments.length > 1) fromSegments.pop();
        } else if (segment !== '.' && segment !== '') {
            fromSegments.push(segment);
        }
    }

    return fromSegments.join('/') || '/';
}

export function toLocation(to: NavigationTarget, current: AppLocation, state: unknown = null): AppLocation {
    const resolved = resolvePath(to, current.pathname);
    const parsed = typeof resolved === 'string' ? parsePath(resolved) : resolved;

    return {
        hash: parsed.hash ?? '',
        key: '',
        pathname: parsed.pathname ?? current.pathname,
        search: parsed.search ?? '',
        state,
    };
}

export function locationsEqual(left: AppLocation, right: AppLocation): boolean {
    return (
        left.pathname === right.pathname &&
        left.search === right.search &&
        left.hash === right.hash &&
        left.state === right.state
    );
}

/**
 * Whether navigating to `next` changes nothing: the same URL and equal history state. Routers treat
 * such a navigation as re-entering the current location rather than adding a history entry
 * (TanStack Router's `commitLocation`); keys never take part.
 */
export function isSameLocation(next: AppLocation, current: AppLocation): boolean {
    return (
        next.pathname === current.pathname &&
        next.search === current.search &&
        next.hash === current.hash &&
        isEqual(next.state ?? null, current.state ?? null)
    );
}
